/**
 * Performance, Summary tab: its types and routes. The CMS metrics come from the benchmark
 * (performance-types.ts) and the dollars from the capture summary; what Aithora measures
 * from its own care records (falls, hospitalizations, readmissions, mood) is served by
 * GET /performance/outcomes (src/aithora/v3/finance/performance-board.ts).
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type { PeerTier } from './performance-types';

/** One outcome Aithora measures from its own care records, ranked against the peer set. */
export interface OutcomeMeasure {
  value: number | null;
  /** 'per_1k_rd' (per 1,000 resident-days) or 'pct'. */
  unit: 'per_1k_rd' | 'pct';
  /** 0..100 (NOT 0..1) against the other Aithora buildings, 100 = best; null below the reporting floor or with fewer than 5 peers. */
  percentile: number | null;
  basis: 'measured' | 'modeled' | 'sample';
  /** The published reference printed beside it, with its source and date (src/aithora/reference-benchmarks.ts). */
  reference?: { value: number; label: string; source?: string; url?: string; asOf?: string } | null;
  /** Why the value or percentile is withheld, when it is. */
  reason?: string;
}

/** GET /api/aithora/performance/outcomes?facility=&tier=&caseMix= */
export interface PerformanceOutcomes {
  facilityId: string | null;
  window: { from: string; to: string; days: number };
  residentDays: number | null;
  /** Resident-days imputed for roster residents with no census row. */
  imputedResidentDays?: number;
  /** The care measures rank against the Aithora network; no CMS peer tier reports them. */
  peerScope?: 'aithora_network';
  peerNote?: string;
  measures: {
    hospitalization: OutcomeMeasure;
    injuriousFalls: OutcomeMeasure;
    falls: OutcomeMeasure;
    readmission: OutcomeMeasure;
    /** Capture rate's percentile against peers (the rate itself is the capture summary's). */
    capture: OutcomeMeasure;
  };
  /** PHQ-9 cohort: residents screened, the reporting floor, and responders scoring 10 or more. */
  phq9: { cohort: number; floor: number; responders: number | null; basis: 'measured' | 'sample' };
  openIncidents: number | null;
  /** The benchmark composite (0..100) carried 30 days forward along the last two recorded quarters (MODELED); null without two quarters. */
  forecast: { percentile: number; direction: 'up' | 'down'; basis: 'modeled' } | null;
  forecastNote?: string | null;
  /** CMS SNF value-based purchasing direction this week. Null: no VBP feed is connected. */
  vbp: { improving: number; worsening: number; measures: number; basis: 'modeled' } | null;
  vbpNote?: string;
  /** The margin ladder's model inputs, in cents (MODELED): CMS cost-report revenue / 12, residents not yet
   *  eligible for RPM 99457 at its CMS rate, and the eligible pool scaled to the design's $123-$300 band top.
   *  Null when no CMS cost-report revenue is on file. */
  ladder: { revenueMonthlyCents: number; rpmHeadroomCents: number; bandTopCents: number; basis: 'modeled' } | null;
  ladderNote?: string;
  real: boolean;
}

/** GET /performance/outcomes: Aithora's own care measures for one building, ranked. */
export const performanceOutcomes = route<PerformanceOutcomes, { facility?: string; tier?: PeerTier; caseMix?: boolean }>()(
  'GET',
  `${AITHORA_PREFIX}/performance/outcomes`,
  { honest: true },
);
